// Lot projections (pre-validation PR 7, v2.2 findings #82).
//
// Where each Active lot is heading: fish weight grown with a thermal growth
// coefficient (TGC), stock reduced by the lot's own recorded mortality, and
// feed added up day by day from the SFR matrix — until the harvest target
// weight. Office-side only (Lotes, Reportes/Panel); never shown in Crew Mode
// (Charter O-1). Pure and deterministic: callers inject every input.
//
// Growth (user decision 2026-09-26: "TGC from samplings"):
//   TGC = (W2^(1/3) − W1^(1/3)) / Σ(daily °C) × 1000, from the lot's last two
//   samplings and the unit's water temperatures in between. A lot with a
//   single sampling (or an implausible observed value) uses the facility's
//   TGC set in Editar instalación. Future days use the mean temperature of
//   the last 14 days of readings (30 days, then the latest reading, as
//   fallbacks).
//
// Mortality (user decision 2026-09-26, findings #74): the daily rate uses
// only days that have a mortality record — "Sin mortalidad" counts as a
// recorded 0, "No se pudo revisar" and blank days do not — and always
// reports how many days of the window were recorded. Under 70 % the
// projection is marked low confidence.

export type DatedValue = { date: string; value: number }

export type LotProjectionInput = {
  /** Samplings (average weight in g), any order; non-positive values are ignored. */
  samplings: DatedValue[]
  /** Water temperature readings (°C) for the lot's unit, any order. */
  temperatures: DatedValue[]
  /** Current stock (fish) and the day it refers to. */
  stock: number | null
  /** Days with a mortality record in the window (deaths that day; 0 = "Sin mortalidad"). */
  mortalityRecordedDays: DatedValue[]
  /** Number of calendar days in the mortality window the recorded days come from. */
  mortalityWindowDays: number
  targetWeightG: number | null
  /** Facility TGC fallback (office-set), e.g. 2.5. */
  officeTgc: number | null
  /** SFR (% of biomass per day) for a weight and temperature, or null when no matrix applies. */
  sfrAt: (weightG: number, temperatureC: number) => number | null
  /** "Today" as YYYY-MM-DD. */
  today: string
  /**
   * Hatchery v0 (findings #84): the estimated fry weight at first feeding,
   * from the egg size. Used only while the lot has no sampling of its own
   * (user decision 2026-09-29: projection-only, never saved as a record).
   */
  estimatedStartWeight?: DatedValue | null
  /** Eggs or yolk-sac alevins: no feeding or growth yet, so no projection. */
  hatcheryStage?: boolean
}

export type LotProjectionStatus =
  | 'ok'
  | 'noSampling'
  | 'noGrowthRate'
  | 'noTemperature'
  | 'noStock'
  | 'hatcheryStage'

export type LotProjection = {
  status: LotProjectionStatus
  lastSamplingDate: string | null
  lastSamplingWeightG: number | null
  estimatedWeightTodayG: number | null
  weightIn30DaysG: number | null
  tgc: number | null
  tgcSource: 'observed' | 'office' | null
  meanTemperatureC: number | null
  mortalityRatePctPerDay: number | null
  mortalityDaysRecorded: number
  mortalityWindowDays: number
  mortalityCompletenessPct: number | null
  targetWeightG: number | null
  daysToHarvest: number | null
  harvestDate: string | null
  stockAtHarvest: number | null
  biomassNowKg: number | null
  biomassAtHarvestKg: number | null
  feedToHarvestKg: number | null
  projectedFcr: number | null
  /** Reasons the office should read the figures with care. */
  cautions: LotProjectionCaution[]
}

export type LotProjectionCaution =
  | 'lowMortalityCompleteness'
  | 'noMortalityRecords'
  | 'officeTgc'
  | 'oldSampling'
  | 'noTarget'
  | 'targetReached'
  | 'horizonReached'
  | 'noSfr'
  | 'unusualFcr'
  | 'estimatedStartWeight'

export const LOW_COMPLETENESS_PCT = 70
export const MAX_PROJECTION_DAYS = 730
const OLD_SAMPLING_DAYS = 30
const MIN_TGC_INTERVAL_DAYS = 7
const PLAUSIBLE_TGC = { min: 0.05, max: 6 }
// A projected FCR outside this range means the inputs disagree (e.g. fast
// observed growth vs. a low SFR matrix): shown as a caution, never hidden.
const USUAL_FCR = { min: 0.8, max: 2.5 }
const DAY_MS = 86_400_000

export const dayMs = (isoDay: string): number => Date.parse(`${isoDay.slice(0, 10)}T00:00:00Z`)
export const addDays = (isoDay: string, days: number): string => new Date(dayMs(isoDay) + days * DAY_MS).toISOString().slice(0, 10)
export const daysBetween = (from: string, to: string): number => Math.round((dayMs(to) - dayMs(from)) / DAY_MS)

export const normalizeSeries = (series: DatedValue[], predicate: (value: number) => boolean): DatedValue[] => {
  const byDay = new Map<string, number>()
  for (const point of series) {
    const day = String(point.date ?? '').slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isFinite(point.value) || !predicate(point.value)) continue
    byDay.set(day, point.value)
  }
  return [...byDay.entries()].map(([date, value]) => ({ date, value })).sort((a, b) => a.date.localeCompare(b.date))
}

/** Temperature for a day: the latest reading on or before it, else the first reading after it. */
export const temperatureOn = (temps: DatedValue[], day: string): number | null => {
  if (temps.length === 0) return null
  let candidate: number | null = null
  for (const point of temps) {
    if (point.date <= day) candidate = point.value
    else break
  }
  return candidate ?? temps[0].value
}

export const degreeDays = (temps: DatedValue[], from: string, to: string): number | null => {
  const days = daysBetween(from, to)
  if (days <= 0 || temps.length === 0) return null
  let total = 0
  for (let offset = 0; offset < days; offset += 1) {
    const temperature = temperatureOn(temps, addDays(from, offset))
    if (temperature === null) return null
    total += Math.max(0, temperature)
  }
  return total
}

export const computeTgc = (w1: number, w2: number, sumDegreeDays: number): number | null => {
  if (!(w1 > 0) || !(w2 > 0) || !(sumDegreeDays > 0)) return null
  return ((Math.cbrt(w2) - Math.cbrt(w1)) / sumDegreeDays) * 1000
}

export const growWeightByTgc = (weightG: number, tgc: number, temperatureC: number): number => {
  const next = Math.cbrt(weightG) + (tgc / 1000) * Math.max(0, temperatureC)
  return next * next * next
}

export const meanRecentTemperature = (temps: DatedValue[], today: string): number | null => {
  if (temps.length === 0) return null
  for (const windowDays of [14, 30]) {
    const from = addDays(today, -windowDays)
    const recent = temps.filter((point) => point.date > from && point.date <= today)
    if (recent.length > 0) return recent.reduce((sum, point) => sum + point.value, 0) / recent.length
  }
  const past = temps.filter((point) => point.date <= today)
  return (past[past.length - 1] ?? temps[temps.length - 1]).value
}

const round = (value: number | null, digits: number): number | null => (
  value === null || !Number.isFinite(value) ? null : Number(value.toFixed(digits))
)

export const projectLot = (input: LotProjectionInput): LotProjection => {
  let samplings = normalizeSeries(input.samplings, (value) => value > 0)
  const temps = normalizeSeries(input.temperatures, () => true)
  const recordedDays = normalizeSeries(input.mortalityRecordedDays, (value) => value >= 0)
  const windowDays = Math.max(0, Math.floor(input.mortalityWindowDays))
  const cautions: LotProjectionCaution[] = []

  const empty: LotProjection = {
    status: 'ok',
    lastSamplingDate: null,
    lastSamplingWeightG: null,
    estimatedWeightTodayG: null,
    weightIn30DaysG: null,
    tgc: null,
    tgcSource: null,
    meanTemperatureC: null,
    mortalityRatePctPerDay: null,
    mortalityDaysRecorded: recordedDays.length,
    mortalityWindowDays: windowDays,
    mortalityCompletenessPct: windowDays > 0 ? round((recordedDays.length / windowDays) * 100, 1) : null,
    targetWeightG: input.targetWeightG && input.targetWeightG > 0 ? input.targetWeightG : null,
    daysToHarvest: null,
    harvestDate: null,
    stockAtHarvest: null,
    biomassNowKg: null,
    biomassAtHarvestKg: null,
    feedToHarvestKg: null,
    projectedFcr: null,
    cautions
  }

  if (input.hatcheryStage) return { ...empty, cautions: [], status: 'hatcheryStage' }

  // Mortality rate over recorded days only (findings #74).
  const stock = input.stock !== null && Number.isFinite(input.stock) && input.stock > 0 ? input.stock : null
  const deaths = recordedDays.reduce((sum, day) => sum + day.value, 0)
  let dailyRatePct: number | null = null
  if (recordedDays.length === 0) {
    cautions.push('noMortalityRecords')
  } else if (stock !== null) {
    const averageStock = stock + deaths / 2
    dailyRatePct = (deaths / recordedDays.length / averageStock) * 100
  }
  if (empty.mortalityCompletenessPct !== null && empty.mortalityCompletenessPct < LOW_COMPLETENESS_PCT && recordedDays.length > 0) {
    cautions.push('lowMortalityCompleteness')
  }
  empty.mortalityRatePctPerDay = round(dailyRatePct, 4)

  if (samplings.length === 0 && input.estimatedStartWeight) {
    samplings = normalizeSeries([input.estimatedStartWeight], (value) => value > 0)
    if (samplings.length > 0) cautions.push('estimatedStartWeight')
  }
  const last = samplings[samplings.length - 1]
  if (!last) return { ...empty, status: 'noSampling' }
  empty.lastSamplingDate = last.date
  empty.lastSamplingWeightG = round(last.value, 2)
  if (daysBetween(last.date, input.today) > OLD_SAMPLING_DAYS) cautions.push('oldSampling')

  // Observed TGC: the latest sampling against the most recent earlier one at
  // least a week before it.
  let tgc: number | null = null
  let tgcSource: LotProjection['tgcSource'] = null
  const previous = [...samplings].reverse().find((point) => daysBetween(point.date, last.date) >= MIN_TGC_INTERVAL_DAYS)
  if (previous && last.value > previous.value) {
    const sumDegreeDays = degreeDays(temps, previous.date, last.date)
    const observed = sumDegreeDays === null ? null : computeTgc(previous.value, last.value, sumDegreeDays)
    if (observed !== null && observed >= PLAUSIBLE_TGC.min && observed <= PLAUSIBLE_TGC.max) {
      tgc = observed
      tgcSource = 'observed'
    }
  }
  if (tgc === null && input.officeTgc !== null && input.officeTgc > 0) {
    tgc = input.officeTgc
    tgcSource = 'office'
    cautions.push('officeTgc')
  }
  empty.tgc = round(tgc, 3)
  empty.tgcSource = tgcSource
  if (tgc === null) return { ...empty, status: 'noGrowthRate' }

  const meanTemperature = meanRecentTemperature(temps, input.today)
  empty.meanTemperatureC = round(meanTemperature, 2)
  if (meanTemperature === null) return { ...empty, status: 'noTemperature' }

  // Weight today: grow the last sampling through the days already lived.
  let weightToday = last.value
  const livedDays = daysBetween(last.date, input.today)
  for (let offset = 0; offset < livedDays; offset += 1) {
    const temperature = temperatureOn(temps, addDays(last.date, offset)) ?? meanTemperature
    weightToday = growWeightByTgc(weightToday, tgc, temperature)
  }
  empty.estimatedWeightTodayG = round(weightToday, 2)

  let weightIn30 = weightToday
  for (let day = 0; day < 30; day += 1) weightIn30 = growWeightByTgc(weightIn30, tgc, meanTemperature)
  empty.weightIn30DaysG = round(weightIn30, 2)

  if (stock === null) return { ...empty, status: 'noStock' }
  const biomassNow = (stock * weightToday) / 1000
  empty.biomassNowKg = round(biomassNow, 2)

  const target = empty.targetWeightG
  if (target === null) {
    cautions.push('noTarget')
    return { ...empty, status: 'ok' }
  }
  if (weightToday >= target) {
    cautions.push('targetReached')
    return {
      ...empty,
      daysToHarvest: 0,
      harvestDate: input.today,
      stockAtHarvest: Math.round(stock),
      biomassAtHarvestKg: round(biomassNow, 2),
      feedToHarvestKg: 0
    }
  }

  const survival = 1 - (dailyRatePct ?? 0) / 100
  let weight = weightToday
  let fish = stock
  let feedKg = 0
  let sfrMissing = false
  let day = 0
  while (weight < target && day < MAX_PROJECTION_DAYS) {
    const biomassKg = (fish * weight) / 1000
    const sfr = input.sfrAt(weight, meanTemperature)
    if (sfr === null || !(sfr > 0)) sfrMissing = true
    else feedKg += (biomassKg * sfr) / 100
    fish *= survival
    weight = growWeightByTgc(weight, tgc, meanTemperature)
    day += 1
  }
  if (weight < target) cautions.push('horizonReached')
  if (sfrMissing) cautions.push('noSfr')

  const biomassAtHarvest = (fish * weight) / 1000
  const gain = biomassAtHarvest - biomassNow
  const fcr = !sfrMissing && gain > 0 ? feedKg / gain : null
  if (fcr !== null && (fcr < USUAL_FCR.min || fcr > USUAL_FCR.max)) cautions.push('unusualFcr')
  return {
    ...empty,
    status: 'ok',
    daysToHarvest: weight >= target ? day : null,
    harvestDate: weight >= target ? addDays(input.today, day) : null,
    stockAtHarvest: Math.round(fish),
    biomassAtHarvestKg: round(biomassAtHarvest, 2),
    feedToHarvestKg: sfrMissing ? null : round(feedKg, 1),
    projectedFcr: round(fcr, 3)
  }
}

export const isLowConfidence = (projection: LotProjection): boolean =>
  projection.cautions.some((caution) => (
    caution === 'lowMortalityCompleteness'
    || caution === 'noMortalityRecords'
    || caution === 'officeTgc'
    || caution === 'oldSampling'
    || caution === 'estimatedStartWeight'
  ))
